import React from 'react';
import { CheckCircle2, AlertCircle, Mail, MessageSquare } from 'lucide-react';
import { cn } from '../../lib/utils';

// ---- Types ----

export interface CommTemplate {
  id: string;
  tenantId: string;
  templateKey: string;
  version: number;
  isActive: boolean;
  eventType: string;
  channel: string;
  subjectTemplate: string | null;
  bodyTemplate: string;
  isSystem: boolean;
  createdBy: string | null;
  approvedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---- Event type grouping ----

const EVENT_TYPE_LABELS: Record<string, string> = {
  new_member: 'New Member',
  new_loan: 'New Loan',
  new_account: 'New Account',
  account_closed: 'Account Closed',
};

function formatTemplateKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---- Props ----

interface TemplateLibraryProps {
  templates: CommTemplate[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}

// ---- Component ----

export function TemplateLibrary({ templates, selectedKey, onSelect }: TemplateLibraryProps): React.ReactElement {
  // Group by eventType
  const grouped = templates.reduce<Record<string, CommTemplate[]>>((acc, t) => {
    const group = acc[t.eventType] ?? [];
    group.push(t);
    acc[t.eventType] = group;
    return acc;
  }, {});

  const sortedEventTypes = Object.keys(grouped).sort((a, b) => {
    const order = ['new_member', 'new_loan', 'new_account', 'account_closed'];
    return (order.indexOf(a) ?? 99) - (order.indexOf(b) ?? 99);
  });

  if (templates.length === 0) {
    return (
      <div className="p-4 text-sm text-ink-muted text-center">
        No templates found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-3">
      {sortedEventTypes.map((eventType) => {
        const group = grouped[eventType] ?? [];
        return (
          <div key={eventType}>
            {/* Section heading */}
            <p className="px-2 mb-1.5 text-xs font-semibold text-ink-muted uppercase tracking-wider">
              {EVENT_TYPE_LABELS[eventType] ?? eventType}
            </p>

            {/* Template cards */}
            <div className="flex flex-col gap-1">
              {group.map((t) => (
                <button
                  key={t.templateKey}
                  type="button"
                  onClick={() => onSelect(t.templateKey)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-lg border transition-colors',
                    selectedKey === t.templateKey
                      ? 'border-brand bg-info-soft/50'
                      : 'border-transparent hover:border-line hover:bg-bg',
                  )}
                >
                  {/* Template name */}
                  <p className="text-sm font-medium text-ink mb-1 truncate">
                    {formatTemplateKey(t.templateKey)}
                  </p>

                  {/* Badges row */}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {/* Channel badge */}
                    {t.channel === 'email' ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-brand-tint text-brand-dark border border-info-soft">
                        <Mail className="h-3 w-3" aria-hidden />
                        Email
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-pass-soft text-pass border border-pass-soft">
                        <MessageSquare className="h-3 w-3" aria-hidden />
                        SMS
                      </span>
                    )}

                    {/* System badge */}
                    {t.isSystem && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-sunken text-ink-muted border border-line">
                        System
                      </span>
                    )}

                    {/* Version */}
                    <span className="text-xs text-ink-muted">v{t.version}</span>

                    {/* Approval status */}
                    {t.approvedBy !== null ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-pass ml-auto flex-shrink-0" aria-label="Approved" />
                    ) : (
                      <AlertCircle className="h-3.5 w-3.5 text-warn ml-auto flex-shrink-0" aria-label="Pending approval" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
